import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, WifiOff } from "lucide-react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { CustomerGate, StaffGate } from "../components/AuthGate";
import { PageLoader } from "../components/StateViews";
import { WorkspaceWebApp } from "../components/WorkspaceWebApp";
import { SessionProvider } from "../context/SessionContext";
import { ThemeProvider } from "../context/ThemeContext";
import { ToastProvider } from "../context/ToastContext";
import { AgentShell } from "../layouts/AgentShell";
import { CustomerShell } from "../layouts/CustomerShell";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import { AgentAnalyticsPage } from "../pages/agent/AgentAnalyticsPage";
import { AgentCustomersPage } from "../pages/agent/AgentCustomersPage";
import { AgentCustomerDetailPage } from "../pages/agent/AgentCustomerDetailPage";
import { AgentDashboardPage } from "../pages/agent/AgentDashboardPage";
import { AgentNewTicketPage } from "../pages/agent/AgentNewTicketPage";
import { AgentNotificationsPage } from "../pages/agent/AgentNotificationsPage";
import { AgentSettingsPage } from "../pages/agent/AgentSettingsPage";
import { AgentSpamPage } from "../pages/agent/AgentSpamPage";
import { AgentTicketDetailPage } from "../pages/agent/AgentTicketDetailPage";
import { AgentTicketExportPage } from "../pages/agent/AgentTicketExportPage";
import { AgentTicketsPage } from "../pages/agent/AgentTicketsPage";
import { AgentLoginPage } from "../pages/agent/StaffLoginPage";
import { HomePage } from "../pages/customer/HomePage";
import { NewTicketPage } from "../pages/customer/NewTicketPage";
import { ProfilePage } from "../pages/customer/ProfilePage";
import { TicketDetailPage } from "../pages/customer/TicketDetailPage";
import { TicketsPage } from "../pages/customer/TicketsPage";

class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Support UI error", error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <main className="fatal-error">
        <span><AlertTriangle aria-hidden="true" /></span>
        <h1>The support app could not open</h1>
        <p>Your ticket data is safe. Reload the page to try again.</p>
        <button className="button button--primary" type="button" onClick={() => window.location.reload()}>Reload support</button>
      </main>
    );
  }
}

function OnlineStatus() {
  const online = useOnlineStatus();
  if (online) return null;
  return <div className="offline-banner" role="status"><WifiOff aria-hidden="true" size={16} /> You’re offline. Draft text stays here, but sending will wait for a connection.</div>;
}

function RoutedApplication() {
  const location = useLocation();
  const family = location.pathname.startsWith("/agent") ? "staff" : "customer";
  return (
    <SessionProvider key={family}>
      <OnlineStatus />
      <WorkspaceWebApp />
      <Routes>
        <Route path="/agent/login" element={<AgentLoginPage />} />
        <Route path="/agent/tickets/:id/export" element={<StaffGate><AgentTicketExportPage /></StaffGate>} />
        <Route path="/agent" element={<StaffGate><AgentShell /></StaffGate>}>
          <Route index element={<AgentDashboardPage />} />
          <Route path="tickets" element={<AgentTicketsPage />} />
          <Route path="tickets/new" element={<AgentNewTicketPage />} />
          <Route path="tickets/:id" element={<AgentTicketDetailPage />} />
          <Route path="spam" element={<AgentSpamPage />} />
          <Route path="customers" element={<AgentCustomersPage />} />
          <Route path="customers/:id" element={<AgentCustomerDetailPage />} />
          <Route path="analytics" element={<AgentAnalyticsPage />} />
          <Route path="notifications" element={<AgentNotificationsPage />} />
          <Route path="settings" element={<AgentSettingsPage />} />
          <Route path="*" element={<Navigate to="/agent" replace />} />
        </Route>
        <Route path="/" element={<CustomerShell />}>
          <Route index element={<HomePage />} />
          <Route path="tickets" element={<TicketsPage />} />
          <Route path="tickets/:id" element={<TicketDetailPage />} />
          <Route path="new" element={<NewTicketPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="auth/sso/callback" element={<CustomerGate><PageLoader label="Completing sign-in…" /></CustomerGate>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </SessionProvider>
  );
}

export function App() {
  return (
    <AppErrorBoundary>
      <ThemeProvider>
        <ToastProvider>
          <BrowserRouter><RoutedApplication /></BrowserRouter>
        </ToastProvider>
      </ThemeProvider>
    </AppErrorBoundary>
  );
}
